import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { CurrentUserStore } from '@pinnakl/core/data-providers';
import { isNeitherNullNorUndefined } from '@pinnakl/shared/util-helpers';
import { merge, of } from 'rxjs';
import { filter, map, switchMap, take, timeout } from 'rxjs/operators';
import { AUTH_RESOLUTION_TIMEOUT_MS, AuthService } from './auth.service';

export const AuthenticatedGuardCanActivate: CanActivateFn = () => {
  const authService = inject(AuthService);
  const currentUserStore = inject(CurrentUserStore);

  if (authService.authFailure()) return of(false);

  const userInfo$ = merge(
    toObservable(currentUserStore.currentUserInfo).pipe(filter(isNeitherNullNorUndefined), map(Boolean)),
    toObservable(authService.authFailure).pipe(
      filter(isNeitherNullNorUndefined),
      map(() => false),
    ),
  ).pipe(
    take(1),
    timeout({
      first: AUTH_RESOLUTION_TIMEOUT_MS,
      with: () => {
        authService.reportBackstop('guard:user-info-timeout');
        authService.redirectToAuthApp();
        return of(false);
      },
    }),
  );

  return authService.isOidcAuthenticated().pipe(
    take(1),
    switchMap(isAuthenticated => {
      if (isAuthenticated) {
        const currentUserInfoLoaded = currentUserStore.currentUserInfoLoaded();
        const currentUserInfoLoading = currentUserStore.currentUserInfoLoading();

        if (!currentUserInfoLoaded && !currentUserInfoLoading) {
          authService.silentlyFetchUser();
        }

        return userInfo$;
      }

      const { search } = new URL(window.location.href);
      const searchParams = new URLSearchParams(search);
      const hasCode = searchParams.has('code');
      if (hasCode) {
        authService.checkAuthCodeAndProceedAuth();
        return userInfo$;
      }
      if (authService.isCompletingLogin) {
        return userInfo$;
      }
      currentUserStore.removeUser();
      authService.redirectToAuthApp();
      return of(false);
    }),
  );
};

export const AuthenticatedGuardCanActivateChild: CanActivateChildFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => AuthenticatedGuardCanActivate(route, state);

export const NoAuthenticatedGuardCanActivate: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  return authService.isOidcAuthenticated().pipe(
    take(1),
    map(isAuthenticated => {
      if (isAuthenticated) {
        return router.createUrlTree(['/']);
      }
      return true;
    }),
  );
};
